import React, { PropsWithChildren, useState, ReactNode } from 'react';
import { Configuration } from '../data/configuration.ts'
import { Utilities } from '../utilities/utilities.js';
import Modal, { ModalContent } from './ui/modal.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export interface ItemProps {
	id: string;
	imageFilePath: string;
	isDisabled?: boolean;
	clickable?: boolean;
}

interface SectionPanelProps {
	subHeading: string;
	shouldStartClosed?: boolean;
	sectionValue?: ReactNode;
	border?: boolean;
	arrowColour?: string; 
	sectionPrice?: number;
	modalContent?: ModalContent[];
	canRender?: boolean;
	onPanelOpenClose?: (isOpen: boolean) => void;
	isClosed?: boolean;
}

interface SectionItemProps {
	id: string;
	itemName: string;
	imageFilePath: string;
	isSelected: boolean;
	isDisabled?: boolean;
	onItemClicked: (id: string) => void;
	showName?: boolean;
	itemPrice?: number;
	shouldEnlarge?:boolean;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

/*
 * SectionPanel Component:
 *
 * Defines a collapsible section panel with a header and content area. The header can optionally display a subheading, price, and modal trigger.
 * The content area can render any child components passed to it.
 *
 * The panel can be toggled open or closed by clicking the header, and can be used as a standalone panel, or as part of an accordion control with multiple panels
 * (in which case the parent component should manage the open/closed state and pass it via props). For this reason the open/closed state can be controlled by
 * the parent component via the 'isClosed' prop, or managed internally if the parent does not provide this prop.
 *
 */
export function SectionPanel(props: PropsWithChildren<SectionPanelProps>) {

	// states.
	const [internalClosed, setInternalClosed] = useState(
		props.shouldStartClosed ?? false
	);

	// if parent provides isClosed, use it, otherwise fall back to internal state.
	const isClosed = props.isClosed ?? internalClosed;
	
	// toggles the panel between open and closed.
	const handleToggleOpen = () => {

		// toggle the closed state.
		const newClosed = !isClosed;

		// notify parent if callback provided.
		props.onPanelOpenClose?.(!newClosed);

		// only update internal state if uncontrolled.
		if (props.isClosed === undefined) {
			setInternalClosed(newClosed);
		}
	};

	// configure arrow colour for use in summary.
	let arrowColour = '';
	if (props.arrowColour) {
		arrowColour = `-${props.arrowColour}`;
	}

	// return the HTML.
	return (
		<div className={`view-content-section${!isClosed ? ' open' : ''} ${props.border ? 'border' : ''}`}>
			<div className={`view-content-section-header ${props.border ? 'border' : ''} pointer`} onClick={handleToggleOpen}>
				<h3 className="sub-heading">
					{props.subHeading}
					{props.sectionPrice && props.sectionPrice > 0 && ' ' + Utilities.asCurrency(props.sectionPrice, Configuration.regionCode)}
				</h3>
				{props.sectionValue}
				<div className="view-content-section-header-controls">
					{(props.modalContent && props.modalContent.length > 0) && (
						<Modal 
							content={props.modalContent}
							trigger={(onClick: () => void) => (
								<a
									role="button"
									onClick={(e: React.MouseEvent) => {
										e.stopPropagation();
										onClick();
									}}
								>
									<img src="assets/images/icons/info.svg" width="25" height="25" alt="Information" />
								</a>
							)}
						/>
					)}
					<a role="button" >
						{!isClosed ? (
							<img src={`assets/images/icons/up-arrow${arrowColour}.svg`} width="18" height="10" alt="Close Panel" />
						) : (
							<img src={`assets/images/icons/down-arrow${arrowColour}.svg`} width="18" height="10" alt="Open Panel" />
						)}
					</a>
				</div>
			</div>

			<div className="view-content-section-panel">
				{props.canRender !== undefined && !props.canRender &&
					(
						<Modal 
							content={[{ title: '', body: 'The app is still in Beta, some features are still in development.' }]}
							trigger={(onClick: () => void) => (
								<div className="render-unavailable-container">
									<p className="render-unavailable-text">Render currently not available. </p>
									<a
										role="button"
										onClick={(e: React.MouseEvent) => {
											e.stopPropagation();
											onClick();
										}}
									>
										<img src="assets/images/icons/info.svg" width="25" height="25" alt="Information" />
									</a>
								</div>
							)}
						/>
					)}

				{props.children}
			</div>
		</div>
	);
}

export function SectionItem(props: PropsWithChildren<SectionItemProps>) {

	// if an enabled item is clicked...
	const handleItemClicked = (event: React.MouseEvent<HTMLElement>) => {

		// get the item ID from the 'data-id' element, and if found, call the callback fn, passing the ID.
		const id = event.currentTarget.dataset.id;
		if (id) {
			props.onItemClicked(id);
		}

		// or log an error.
		else {
			console.error('Mising data-id on element: ', event.currentTarget);
		}
	};

	// return the HTML.
	return (
		<a
			role="button"
			className={`view-content-item${props.isSelected ? ' selected' : ''}${props.isDisabled ? ' disabled' : ''}`}
			data-id={props.id}
			onClick={handleItemClicked}
		>
			<img draggable={false} src={props.imageFilePath} width="90" height="90" style={{ width: props.shouldEnlarge ? '24vw' : '20vw' }} alt={props.itemName} />
			<p style={{ display: props.showName ? 'block' : 'none' }} className="view-content-item-name">{props.itemName}</p>
			<p style={{ display: props.itemPrice && props.itemPrice > 0 ? 'block' : 'none' }} className="view-content-item-price">{props.itemPrice ? Utilities.asCurrency(props.itemPrice, Configuration.regionCode) : ''}</p>
			{props.children}
		</a>
	);
}
